import { Injectable, OnDestroy } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { delay, exhaustMap, filter, repeat, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { isCanvasRendererHost } from 'src/app/canvas-renderer/canvas-renderer-host';
import { catchAppError } from 'src/app/shared/utils';
import { environment } from 'src/environments/environment';
import { AppService, ETLRunStatus, VolatileAppSettingsDto } from '../api.services';

const VOLATILE_SETTINGS_PULL_INTERVAL = environment.pullRates.volatileSettings;

export interface ListHealthData {
  application: string;
  jobName: string;
  startTime: string;
  endTime: string | undefined;
  duration: string;
  status: ETLRunStatus;
}

@Injectable({
  providedIn: 'root'
})
export class VolatileSettingsDataService implements OnDestroy {
  private healthDataSubject: BehaviorSubject<ListHealthData[]> = new BehaviorSubject<ListHealthData[]>(null);
  public readonly healthData$: Observable<ListHealthData[]> = this.healthDataSubject.asObservable();

  private volatileSettingsSubject: BehaviorSubject<VolatileAppSettingsDto> = new BehaviorSubject<VolatileAppSettingsDto>(null);
  public readonly volatileSettings$: Observable<VolatileAppSettingsDto> = this.volatileSettingsSubject.asObservable();

  private bannerDismissedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly bannerDismissed$: Observable<boolean> = this.bannerDismissedSubject.asObservable();

  private toggleDataHealthModalSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public dataHealthModalStatus$ = this.toggleDataHealthModalSubject.asObservable();

  private destroy: Subject<void> = new Subject<void>();

  private dataLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public dataLoaded$ = this.dataLoadedSubject.asObservable();

  constructor(
    private appService: AppService,
    private authService: AuthService) {
    if (isCanvasRendererHost)
      return;

    of(null).pipe(
      filter(() => VOLATILE_SETTINGS_PULL_INTERVAL != null),
      
      exhaustMap(() => {
        if (this.authService.ready)
          return this.appService.getVolatileSettings().pipe(
            tap(vs => {
        const healthData = this.parseHealthData(vs);
        this.volatileSettingsSubject.next(vs);
        this.healthDataSubject.next(healthData);
        this.dataLoadedSubject.next(true);
      }),
          );

        return of(null)
      }),

      // Don't show error snack for the pulled settings
      // We just consume the error and retry
      // If we got auth'ed out, any other operation will log us out
      catchAppError(null, { showErrorSnack: false }),


      delay(VOLATILE_SETTINGS_PULL_INTERVAL),
      repeat(),

      takeUntil(this.destroy)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy.next();
  }

  public openHealthModal(): void {
    this.toggleDataHealthModalSubject.next(true);
  }

  public setDataHealthBannerDismissed(dismiss: boolean): void {
    this.bannerDismissedSubject.next(dismiss);
  }

  private parseHealthData(volatileSettings: VolatileAppSettingsDto): ListHealthData[] {
    const tempData: ListHealthData[] = [];
    volatileSettings.spendETLStatus?.historicRuns?.map(r => {
      tempData.push({
        application: 'Spend',
        jobName: 'Core Dataset Generation',
        status: r.etlRunStatus,
        startTime: r.runStartDate,
        endTime: r.runEndDate !== null ? r.runEndDate : undefined,
        duration: r.runEndDate !== null ? this.calculateDurationBetweenDates(r.runStartDate, r.runEndDate) : ''
      });
    });
    tempData.push({
      application: 'Spend',
      jobName: 'Core Dataset Generation',
      status: volatileSettings.spendETLStatus?.etlRunStatus,
      startTime: volatileSettings.spendETLStatus?.startDate,
      endTime: volatileSettings.spendETLStatus?.endDate !== null ? volatileSettings.spendETLStatus?.endDate : undefined,
      duration: volatileSettings.spendETLStatus?.endDate !== null ?
        this.calculateDurationBetweenDates(volatileSettings.spendETLStatus?.startDate, volatileSettings.spendETLStatus?.endDate) : ''
    });
    return tempData.sort((a, b) => b.startTime.localeCompare(a.endTime)) ?? [];
}

  private calculateDurationBetweenDates(start: string, end: string | undefined): string {
    if (end === undefined) {
      return '';
    }

    const s = moment(start);
    const e = moment(end);

    const duration = moment.duration(e.diff(s));
    const minutes = Math.abs(Math.round(duration.asMinutes()));
    const seconds = Math.abs(Math.round(duration.asSeconds()) - minutes * 60);

    return ((minutes < 10) ? ('0' + minutes) : minutes) + ':' + ((seconds < 10) ? ('0' + seconds) : seconds);
  }
}
